import React, { useState, useEffect } from 'react';
import { Hospital, HospitalPreferences, Room, Patient, HospitalGroup } from '../types';
import { X, Save, Building, Phone, Key, ShieldCheck, Mail, MapPin, Monitor, ToggleLeft, ToggleRight, Layout, Palette, Grid, Plus, Trash2, Edit2, Check, Upload, ImageIcon, LinkIcon, AlertCircle, User, Eye, EyeOff, Lock, RefreshCw } from 'lucide-react';
import * as storageService from '../services/storageService';

interface HospitalModalProps {
    isOpen: boolean;
    onClose: () => void;
    hospital?: Hospital | null; // If null, creating new
    groups?: HospitalGroup[];
    onSave: () => void;
}

const HospitalModal: React.FC<HospitalModalProps> = ({ isOpen, onClose, hospital, groups = [], onSave }) => {
    // General
    const [name, setName] = useState('');
    const [maxRooms, setMaxRooms] = useState('5');
    const [handle, setHandle] = useState('');
    const [isActive, setIsActive] = useState(true);

    // Organization
    const [organizationTier, setOrganizationTier] = useState<'INDEPENDENT' | 'GROUP_MEMBER'>('INDEPENDENT');
    const [groupId, setGroupId] = useState('');

    // Contact
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [address, setAddress] = useState('');

    // Operations (PIN Maestro removed from here, now in Access Center)

    // Branding
    const [primaryColor, setPrimaryColor] = useState('#5B65DC');
    const [secondaryColor, setSecondaryColor] = useState('#122056');
    const [logoUrl, setLogoUrl] = useState('');
    const [logoDarkUrl, setLogoDarkUrl] = useState('');
    const [backgroundColor, setBackgroundColor] = useState<'#ffffff' | '#f2f2f7'>('#f2f2f7');
    const [handleError, setHandleError] = useState('');

    // Rooms & Patients
    const [rooms, setRooms] = useState<Room[]>([]);
    const [patients, setPatients] = useState<Patient[]>([]);
    const [editingRoomId, setEditingRoomId] = useState<string | null>(null);
    const [editRoomName, setEditRoomName] = useState('');
    const [newRoomName, setNewRoomName] = useState('');

    // Preferences
    const [preferences, setPreferences] = useState<HospitalPreferences>({
        autoDeleteHours: 3,
        allowFamilyNotifications: true,
        showProcedureName: true,
        showPatientName: true,
        customWelcomeMessage: ''
    });

    // Public Visibility
    const [publicVisibility, setPublicVisibility] = useState<'public' | 'pin' | 'private'>('pin');
    const [receptionPin, setReceptionPin] = useState('');
    // Features / Modules
    const [features, setFeatures] = useState<string[]>(['ADS']);

    const [activeTab, setActiveTab] = useState<'general' | 'branding' | 'rooms' | 'contact' | 'ops' | 'prefs' | 'modules'>('general');

    useEffect(() => {
        if (hospital) {
            setName(hospital.name || '');
            setMaxRooms(hospital.maxRooms?.toString() || '1');
            setHandle(hospital.handle || '');
            setIsActive(hospital.isActive ?? true);
            setContactName(hospital.contactName || '');
            setContactEmail(hospital.contactEmail || '');
            setContactPhone(hospital.contactPhone || '');
            setAddress(hospital.address || '');
            setOrganizationTier((hospital as any).organizationTier || 'INDEPENDENT');
            setGroupId((hospital as any).groupId || '');
            setPrimaryColor(hospital.branding.primaryColor);
            setSecondaryColor(hospital.branding.secondaryColor);
            setLogoUrl(hospital.branding.logoUrl || '');
            setLogoDarkUrl(hospital.branding.logoDarkUrl || '');
            setBackgroundColor(hospital.branding.backgroundColor as any || '#f2f2f7');
            setPreferences(hospital.preferences || {
                autoDeleteHours: 3,
                allowFamilyNotifications: true,
                showProcedureName: true,
                showPatientName: true,
                requirePrivacyNotice: false
            });
            setPublicVisibility(hospital.publicVisibility || 'pin');
            setReceptionPin(hospital.receptionPin || '');
            setFeatures(hospital.features || ['ADS']);
            loadData(hospital.id);
        } else {
            // Defaults for new
            setName('');
            setMaxRooms('5');
            setHandle('');
            setIsActive(true);
            setContactName('');
            setContactEmail('');
            setContactPhone('');
            setAddress('');
            setOrganizationTier('INDEPENDENT');
            setGroupId('');
            setPrimaryColor('#5B65DC');
            setSecondaryColor('#122056');
            setLogoUrl('');
            setLogoDarkUrl('');
            setBackgroundColor('#f2f2f7');
            setRooms([]);
            setPatients([]);
            setPreferences({
                autoDeleteHours: 3,
                allowFamilyNotifications: true,
                showProcedureName: true,
                showPatientName: true,
                requirePrivacyNotice: false,
                googleReviewThreshold: 5
            });
            setPublicVisibility('pin');
            setReceptionPin('');
            setFeatures(['ADS']);
        }
    }, [hospital, isOpen]);

    const loadData = async (hid: string) => {
        const [r, p] = await Promise.all([
            storageService.getRoomsByHospital(hid),
            storageService.getPatientsByHospital(hid)
        ]);
        setRooms(r);
        setPatients(p);
        // Sync maxRooms count with actual rooms length if editing an existing hospital
        if (hid && hospital) {
            if (r.length === 0 && hospital.maxRooms > 0) {
                // Auto-generate rooms if inconsistencies found
                const newRooms = [];
                for (let i = 1; i <= hospital.maxRooms; i++) {
                    newRooms.push({ id: `${hid}-R${Date.now()}-${i}`, hospitalId: hid, name: `Quirófano ${i}`, currentPatientId: null });
                }
                setRooms(newRooms);
                // Save them
                newRooms.forEach(room => storageService.addRoom(hid, room.name));
            } else if (r.length > 0) {
                setMaxRooms(r.length.toString());
            } else {
                setMaxRooms(hospital.maxRooms?.toString() || '1');
            }
        }
    };

    const handleSave = async () => {
        if (!name.trim()) return alert('El nombre es obligatorio');

        // Validate Handle
        if (handle && (!hospital || handle !== hospital.handle)) {
            const hidd = hospital?.id || 'NEW';
            const exists = await storageService.getHospitals().then(list => list.some(h => h.handle === handle && h.id !== hidd));
            if (exists) {
                setHandleError('Este enlace ya está en uso');
                setActiveTab('branding');
                return;
            }
        }

        const hospitalData: any = {
            id: hospital?.id,
            name,
            maxRooms: parseInt(maxRooms) || 1,
            handle,
            organizationTier,
            groupId: organizationTier === 'GROUP_MEMBER' && groupId ? groupId : null,
            isActive,
            contactName,
            contactEmail,
            contactPhone,
            address,
            branding: {
                primaryColor,
                secondaryColor,
                logoUrl,
                logoDarkUrl,
                backgroundColor
            },
            preferences,
            publicVisibility,
            receptionPin: publicVisibility === 'pin' ? receptionPin : null,
            features
        };

        if (hospital) {
            const updated = { ...hospital, ...hospitalData };
            await storageService.saveHospital(updated);
        } else {
            await storageService.createHospital(name, parseInt(maxRooms), handle, hospitalData);
        }

        onSave();
        onClose();
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setLogoUrl(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleFileUploadDark = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setLogoDarkUrl(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleAddRoom = async () => {
        if (!hospital?.id || !newRoomName.trim()) return;
        await storageService.addRoom(hospital.id, newRoomName);
        setNewRoomName('');
        await loadData(hospital.id);
    };

    const handleDeleteRoom = async (rid: string) => {
        if (window.confirm('¿Eliminar quirófano?')) {
            await storageService.deleteRoom(rid);
            if (hospital?.id) await loadData(hospital.id);
        }
    };

    const saveRoomEdit = async () => {
        if (editingRoomId && editRoomName.trim()) {
            await storageService.updateRoomName(editingRoomId, editRoomName);
            setEditingRoomId(null);
            if (hospital?.id) await loadData(hospital.id);
        }
    };

    if (!isOpen) return null;

    // Helper to find patient in room
    const getPatientInRoom = (roomId: string) => {
        return patients.find(p => p.room === roomId);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        {hospital ? <Building className="w-5 h-5 text-indigo-600" /> : <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xs">+</div>}
                        {hospital ? `Configurar: ${hospital.name}` : 'Registrar Nuevo Hospital'}
                    </h2>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                            <X className="w-5 h-5 text-slate-400" />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-slate-100 px-6 gap-6 overflow-x-auto bg-white">
                    {[
                        { id: 'general', label: 'Básico', icon: Building },
                        { id: 'branding', label: 'Diseño', icon: Palette },
                        { id: 'rooms', label: 'Quirófanos', icon: Grid },
                        { id: 'contact', label: 'Contacto', icon: Phone },
                        { id: 'prefs', label: 'Preferencias', icon: Monitor },
                        { id: 'modules', label: 'Módulos (Admin)', icon: ShieldCheck },
                    ].map(tab => (
                        (!hospital && (tab.id === 'branding' || tab.id === 'rooms')) ? null : (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center gap-2 py-4 border-b-2 text-sm font-bold transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'
                                    }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        )
                    ))}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-8 bg-white">

                    {activeTab === 'general' && (
                        <div className="space-y-6 animate-fade-in">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Nombre Comercial</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => {
                                        const newName = e.target.value;
                                        setName(newName);
                                        if (!hospital) {
                                            setHandle(newName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
                                        }
                                    }}
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-bold text-slate-700"
                                    placeholder="Ej. Hospital Muguerza"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Estado</label>
                                    <button
                                        onClick={() => setIsActive(!isActive)}
                                        className={`w-full p-3 rounded-xl flex items-center justify-between font-bold border transition-all ${isActive ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
                                    >
                                        <span>{isActive ? 'Operativo / Activo' : 'Desactivado'}</span>
                                        {isActive ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                                    </button>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Quirófanos Totales</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="50"
                                        value={maxRooms}
                                        onChange={(e) => setMaxRooms(e.target.value)}
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono font-bold text-slate-700"
                                    />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-100">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" title="Permite asignar este hospital a una cadena administrada por un Group Admin">Cadena Hospitalaria</label>
                                    <select
                                        value={organizationTier}
                                        onChange={(e) => {
                                            setOrganizationTier(e.target.value as 'INDEPENDENT' | 'GROUP_MEMBER');
                                            if (e.target.value === 'INDEPENDENT') setGroupId('');
                                        }}
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-bold text-slate-700"
                                    >
                                        <option value="INDEPENDENT">Independiente (Stand-alone)</option>
                                        <option value="GROUP_MEMBER">Miembro de una Cadena (Grupo)</option>
                                    </select>
                                </div>

                                {organizationTier === 'GROUP_MEMBER' && (
                                    <div className="animate-fade-in">
                                        <label className="block text-xs font-bold text-indigo-500 uppercase tracking-wider mb-2">Cadena a la que pertenece</label>
                                        <select
                                            value={groupId}
                                            onChange={(e) => setGroupId(e.target.value)}
                                            className="w-full p-3 bg-indigo-50 border border-indigo-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-bold text-indigo-700"
                                        >
                                            <option value="">-- Selecciona una Cadena --</option>
                                            {groups.filter(g => g.isActive).map(g => (
                                                <option key={g.id} value={g.id}>{g.name}</option>
                                            ))}
                                        </select>
                                        <p className="text-[9px] text-slate-400 mt-1 uppercase tracking-wider font-bold">El hospital se agrupará visualmente bajo esta Cadena en el Dashboard.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'branding' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-fade-in">
                            <div className="space-y-6">
                                <div>
                                    <label className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"><LinkIcon className="w-4 h-4" /> Enlace (Handle)</label>
                                    <div className="flex items-center gap-1 font-mono text-sm bg-slate-50 p-3 rounded-xl border border-slate-200">
                                        <span className="text-slate-400">/</span>
                                        <input
                                            type="text"
                                            value={handle}
                                            onChange={e => { setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); setHandleError(''); }}
                                            className="bg-transparent border-0 focus:ring-0 p-0 font-bold w-full"
                                        />
                                    </div>
                                    {handleError && <p className="text-[10px] text-red-500 font-bold mt-1 uppercase tracking-tight">{handleError}</p>}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Logo del Hospital (Modo Claro)</label>
                                    <div className="flex items-center gap-4">
                                        <div className="w-16 h-16 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center p-2 overflow-hidden">
                                            {logoUrl ? <img src={logoUrl} className="w-full h-full object-contain" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
                                        </div>
                                        <div className="flex-1">
                                            <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" id="logo-up" />
                                            <label htmlFor="logo-up" className="inline-block px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold cursor-pointer hover:bg-indigo-100 transition-colors">Subir Imagen</label>
                                            <input type="text" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="O pega URL..." className="block w-full mt-2 text-xs p-2 bg-slate-50 border border-slate-200 rounded-lg" />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Logo del Hospital (Modo Oscuro)</label>
                                    <div className="flex items-center gap-4">
                                        <div className="w-16 h-16 bg-slate-800 rounded-xl border border-slate-700 flex items-center justify-center p-2 overflow-hidden">
                                            {logoDarkUrl ? <img src={logoDarkUrl} className="w-full h-full object-contain" /> : <ImageIcon className="w-6 h-6 text-slate-600" />}
                                        </div>
                                        <div className="flex-1">
                                            <input type="file" accept="image/*" onChange={handleFileUploadDark} className="hidden" id="logo-dark-up" />
                                            <label htmlFor="logo-dark-up" className="inline-block px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold cursor-pointer hover:bg-indigo-100 transition-colors">Subir Imagen Oscura</label>
                                            <input type="text" value={logoDarkUrl} onChange={e => setLogoDarkUrl(e.target.value)} placeholder="O pega URL..." className="block w-full mt-2 text-xs p-2 bg-slate-50 border border-slate-200 rounded-lg" />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Color Primario</label>
                                        <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="w-full h-10 rounded-lg cursor-pointer border-0 p-0 bg-transparent" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Color Texto</label>
                                        <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} className="w-full h-10 rounded-lg cursor-pointer border-0 p-0 bg-transparent" />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Fondo de Pantalla</label>
                                    <div className="flex gap-2">
                                        {['#ffffff', '#f2f2f7'].map(c => (
                                            <button
                                                key={c}
                                                onClick={() => setBackgroundColor(c as any)}
                                                className={`flex-1 p-3 rounded-xl border-2 transition-all ${backgroundColor === c ? 'border-indigo-600 bg-indigo-50' : 'border-slate-100'}`}
                                            >
                                                <div className="w-full h-4 rounded mb-1" style={{ backgroundColor: c, border: '1px solid #ddd' }}></div>
                                                <span className="text-[10px] font-bold uppercase">{c === '#ffffff' ? 'Blanco' : 'Gris'}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-50 rounded-2xl p-6 flex flex-col items-center justify-center border border-slate-100">
                                <div className="text-[10px] font-bold text-slate-400 uppercase mb-4 tracking-widest">Vista Previa Mobile</div>
                                <div className="w-48 aspect-[9/16] bg-white rounded-3xl border-4 border-slate-800 shadow-xl relative overflow-hidden flex flex-col" style={{ backgroundColor }}>
                                    <div className="h-4 bg-slate-800 w-1/3 mx-auto rounded-b-xl mb-4"></div>
                                    <div className="px-3 flex-1 flex flex-col items-center justify-center gap-2">
                                        {logoUrl && <img src={logoUrl} className="h-8 object-contain mb-2" />}
                                        <div className="w-full h-8 rounded-lg shadow-sm" style={{ backgroundColor: primaryColor }}></div>
                                        <div className="w-full h-8 bg-white border border-slate-100 rounded-lg"></div>
                                        <div className="text-[8px] font-bold uppercase" style={{ color: secondaryColor }}>{name}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'rooms' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="flex gap-4">
                                <input
                                    type="text"
                                    value={newRoomName}
                                    onChange={e => setNewRoomName(e.target.value)}
                                    placeholder="Nombre del quirófano (Ej. Sala 1)"
                                    className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-bold"
                                    onKeyDown={e => e.key === 'Enter' && handleAddRoom()}
                                />
                                <button onClick={handleAddRoom} className="bg-indigo-600 text-white px-6 rounded-xl font-bold flex items-center gap-2"><Plus className="w-4 h-4" /> Agregar</button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {rooms.map(r => {
                                    const occupant = getPatientInRoom(r.id);
                                    return (
                                        <div key={r.id} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between group relative overflow-hidden">
                                            {occupant && (
                                                <div className="absolute top-0 right-0 px-2 py-0.5 bg-emerald-500 text-white text-[8px] font-bold uppercase tracking-widest rounded-bl-lg">En Uso</div>
                                            )}
                                            {editingRoomId === r.id ? (
                                                <div className="flex items-center gap-2 flex-1">
                                                    <input value={editRoomName} onChange={e => setEditRoomName(e.target.value)} className="flex-1 p-2 bg-white border rounded-lg text-sm font-bold" autoFocus />
                                                    <button onClick={saveRoomEdit} className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><Check className="w-4 h-4" /></button>
                                                    <button onClick={() => setEditingRoomId(null)} className="p-2 bg-slate-200 text-slate-500 rounded-lg"><X className="w-4 h-4" /></button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-indigo-600 shadow-sm border border-slate-100">
                                                                <Grid className="w-4 h-4" />
                                                            </div>
                                                            <span className="font-bold text-slate-700">{r.name}</span>
                                                        </div>
                                                        {occupant && (
                                                            <div className="flex items-center gap-1.5 ml-11 text-[10px] text-slate-400 font-bold uppercase">
                                                                <User className="w-3 h-3 text-emerald-500" /> {occupant.name}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button onClick={() => { setEditingRoomId(r.id); setEditRoomName(r.name); }} className="p-2 text-slate-400 hover:text-indigo-600"><Edit2 className="w-4 h-4" /></button>
                                                        <button onClick={() => handleDeleteRoom(r.id)} className="p-2 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>

                            {rooms.length === 0 && (
                                <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                                    <Grid className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                                    <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">No hay quirófanos configurados</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'contact' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Nombre del Contacto</label>
                                    <div className="relative">
                                        <Building className="w-4 h-4 absolute left-3 top-3.5 text-slate-400" />
                                        <input
                                            type="text"
                                            value={contactName}
                                            onChange={(e) => setContactName(e.target.value)}
                                            className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-medium"
                                            placeholder="Gerente Administrativo"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Email</label>
                                    <div className="relative">
                                        <Mail className="w-4 h-4 absolute left-3 top-3.5 text-slate-400" />
                                        <input
                                            type="email"
                                            value={contactEmail}
                                            onChange={(e) => setContactEmail(e.target.value)}
                                            className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-medium"
                                            placeholder="admin@hospital.com"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Teléfono</label>
                                    <div className="relative">
                                        <Phone className="w-4 h-4 absolute left-3 top-3.5 text-slate-400" />
                                        <input
                                            type="tel"
                                            value={contactPhone}
                                            onChange={(e) => setContactPhone(e.target.value)}
                                            className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-medium"
                                            placeholder="(81) 8888-8888"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Dirección Física</label>
                                <div className="relative">
                                    <MapPin className="w-4 h-4 absolute left-3 top-3.5 text-slate-400" />
                                    <textarea
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-medium"
                                        rows={2}
                                        placeholder="Av. Principal #123..."
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'prefs' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <div>
                                        <div className="font-bold text-slate-700 text-sm">Mostrar Nombre del Paciente</div>
                                        <div className="text-xs text-slate-400">Visible en la pantalla familiar</div>
                                    </div>
                                    <button
                                        onClick={() => setPreferences({ ...preferences, showPatientName: !preferences.showPatientName })}
                                        className={`w-12 h-6 rounded-full transition-colors relative ${preferences.showPatientName ? 'bg-indigo-600' : 'bg-slate-300'}`}
                                    >
                                        <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${preferences.showPatientName ? 'translate-x-6' : 'translate-x-0'}`} />
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <div>
                                        <div className="font-bold text-slate-700 text-sm">Mostrar Procedimiento</div>
                                        <div className="text-xs text-slate-400">Detalle de la cirugía</div>
                                    </div>
                                    <button
                                        onClick={() => setPreferences({ ...preferences, showProcedureName: !preferences.showProcedureName })}
                                        className={`w-12 h-6 rounded-full transition-colors relative ${preferences.showProcedureName ? 'bg-indigo-600' : 'bg-slate-300'}`}
                                    >
                                        <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${preferences.showProcedureName ? 'translate-x-6' : 'translate-x-0'}`} />
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <div>
                                        <div className="font-bold text-slate-700 text-sm">Aviso de Privacidad Obligatorio</div>
                                        <div className="text-xs text-slate-400">Forzar aceptación de uso de datos médicos en registro y portal</div>
                                    </div>
                                    <button
                                        onClick={() => setPreferences({ ...preferences, requirePrivacyNotice: !preferences.requirePrivacyNotice })}
                                        className={`w-12 h-6 rounded-full transition-colors relative ${preferences.requirePrivacyNotice ? 'bg-indigo-600' : 'bg-slate-300'}`}
                                    >
                                        <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${preferences.requirePrivacyNotice ? 'translate-x-6' : 'translate-x-0'}`} />
                                    </button>
                                </div>

                                {/* Google Reviews URL & Threshold */}
                                <div className="pt-4 border-t border-slate-100">
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Google Reviews</label>
                                    <p className="text-[11px] text-slate-400 mb-3">Redirige a los familiares a Google Maps para dejar su reseña cuando dan una alta calificación.</p>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Enlace (URL)</label>
                                            <div className="relative">
                                                <svg className="w-4 h-4 absolute left-3 top-3.5 text-amber-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                                                <input
                                                    type="url"
                                                    value={preferences.googleReviewUrl || ''}
                                                    onChange={(e) => setPreferences({ ...preferences, googleReviewUrl: e.target.value })}
                                                    className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 font-medium text-sm"
                                                    placeholder="https://g.page/r/tu-hospital/review"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Redirigir a partir de:</label>
                                            <select
                                                value={preferences.googleReviewThreshold || 5}
                                                onChange={(e) => setPreferences({ ...preferences, googleReviewThreshold: Number(e.target.value) })}
                                                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 font-medium text-sm text-slate-700"
                                            >
                                                <option value={5}>5 Estrellas (Recomendado)</option>
                                                <option value={4}>4 Estrellas o más</option>
                                                <option value={3}>3 Estrellas o más</option>
                                                <option value={1}>1 Estrella o más (Siempre)</option>
                                            </select>
                                        </div>
                                    </div>
                                    {preferences.googleReviewUrl && (
                                        <div className="mt-3 flex items-center gap-1.5 text-[10px] text-emerald-600 font-bold">
                                            <ShieldCheck className="w-3.5 h-3.5" />
                                            <span>Activo — redirigirá calificaciones de {preferences.googleReviewThreshold || 5}⭐ o más.</span>
                                        </div>
                                    )}
                                </div>

                                {/* Public Visibility Toggle */}
                                <div className="pt-6 border-t border-slate-100">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Eye className="w-4 h-4 text-indigo-600" />
                                        <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Visibilidad de Recepción Digital</span>
                                    </div>
                                    <p className="text-[11px] text-slate-400 mb-4">Controla quién puede ver la página pública de cirugías activas (<code className="bg-slate-100 px-1 py-0.5 rounded text-[10px]">/{handle || 'handle'}</code>).</p>
                                    
                                    <div className="grid grid-cols-3 gap-2">
                                        {[
                                            { value: 'public', label: 'Público', desc: 'Cualquiera puede ver', icon: Eye, color: 'emerald' },
                                            { value: 'pin', label: 'Con PIN', desc: 'Requiere PIN de recepción', icon: Lock, color: 'amber' },
                                            { value: 'private', label: 'Privado', desc: 'Solo vista familiar con código', icon: EyeOff, color: 'red' }
                                        ].map(opt => (
                                            <button
                                                key={opt.value}
                                                onClick={() => setPublicVisibility(opt.value as any)}
                                                className={`p-3 rounded-xl border-2 text-left transition-all ${
                                                    publicVisibility === opt.value
                                                        ? `border-${opt.color}-500 bg-${opt.color}-50`
                                                        : 'border-slate-100 hover:border-slate-200'
                                                }`}
                                                style={publicVisibility === opt.value ? {
                                                    borderColor: opt.color === 'emerald' ? '#10b981' : opt.color === 'amber' ? '#f59e0b' : '#ef4444',
                                                    backgroundColor: opt.color === 'emerald' ? '#ecfdf5' : opt.color === 'amber' ? '#fffbeb' : '#fef2f2'
                                                } : {}}
                                            >
                                                <opt.icon className="w-4 h-4 mb-1" style={{ color: opt.color === 'emerald' ? '#10b981' : opt.color === 'amber' ? '#f59e0b' : '#ef4444' }} />
                                                <div className="text-xs font-bold text-slate-700">{opt.label}</div>
                                                <div className="text-[9px] text-slate-400">{opt.desc}</div>
                                            </button>
                                        ))}
                                    </div>

                                    {publicVisibility === 'pin' && (
                                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl animate-fade-in">
                                            <label className="block text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">PIN de Recepción</label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={6}
                                                    value={receptionPin}
                                                    onChange={(e) => setReceptionPin(e.target.value.replace(/\D/g, ''))}
                                                    placeholder="1234"
                                                    className="flex-1 p-3 bg-white border border-amber-200 rounded-xl font-mono text-2xl font-black tracking-[0.3em] text-center focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setReceptionPin(String(Math.floor(1000 + Math.random() * 9000)))}
                                                    className="px-4 py-3 bg-amber-100 text-amber-700 rounded-xl font-bold text-xs hover:bg-amber-200 transition-colors flex items-center gap-1"
                                                    title="Generar PIN aleatorio"
                                                >
                                                    <RefreshCw className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <p className="text-[10px] text-amber-600 mt-2 font-medium">Este PIN se entrega al familiar en admisión junto con su código de paciente.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'modules' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 mb-6">
                                <h3 className="text-sm font-black text-indigo-900 mb-1 flex items-center gap-2">
                                    <ShieldCheck className="w-4 h-4" /> Licenciamiento de Módulos
                                </h3>
                                <p className="text-xs text-indigo-700">Como Super Admin, tú controlas qué módulos puede ver y configurar el cliente. Si desactivas un módulo, el hospital no podrá acceder a él, pero tú sí podrás configurarlo en su nombre.</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <button
                                    onClick={() => {
                                        if (features.includes('ADS')) {
                                            setFeatures(features.filter(f => f !== 'ADS'));
                                        } else {
                                            setFeatures([...features, 'ADS']);
                                        }
                                    }}
                                    className={`flex items-start gap-4 p-5 rounded-2xl border-2 transition-all text-left ${
                                        features.includes('ADS') 
                                            ? 'border-indigo-600 bg-indigo-50 shadow-sm' 
                                            : 'border-slate-200 bg-white hover:border-indigo-300'
                                    }`}
                                >
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${features.includes('ADS') ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                        <Monitor className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className={`text-sm font-black mb-1 ${features.includes('ADS') ? 'text-indigo-900' : 'text-slate-700'}`}>Publicidad (TVs)</h4>
                                        <p className="text-[11px] text-slate-500 leading-tight">Permitir al hospital subir sus propios anuncios y ver el rendimiento de las campañas.</p>
                                    </div>
                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${features.includes('ADS') ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300'}`}>
                                        {features.includes('ADS') && <Check className="w-4 h-4" />}
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-3 font-bold text-slate-500 hover:bg-slate-200 rounded-xl transition-colors">
                        Cancelar
                    </button>
                    <button onClick={handleSave} className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-600/20 transition-all active:scale-95 flex items-center gap-2">
                        <Save className="w-5 h-5" />
                        {hospital ? 'Guardar Cambios' : 'Registrar Hospital'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HospitalModal;
